"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TooltipWithContent } from "@/components/ui/tooltip";
import { FileText, Clock, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { loadDraft, EmailDraft } from "@/lib/utils/crm/draft-storage";

interface DraftIndicatorProps {
  type: "compose" | "reply";
  identifier: string; // email address for compose, message ID for reply
  className?: string;
  variant?: "badge" | "button" | "icon";
  onClick?: () => void;
  showTime?: boolean;
}

export function DraftIndicator({
  type,
  identifier,
  className,
  variant = "badge",
  onClick,
  showTime = true,
}: DraftIndicatorProps) {
  const [draft, setDraft] = useState<EmailDraft | null>(null);

  useEffect(() => {
    if (!identifier) return;

    const loadDraftData = () => {
      const draftData = loadDraft(type, identifier);
      setDraft(draftData);
    };

    loadDraftData();

    // Update when window gains focus (in case drafts were modified in another tab)
    const handleFocus = () => loadDraftData();
    window.addEventListener("focus", handleFocus);

    return () => window.removeEventListener("focus", handleFocus);
  }, [type, identifier]);

  if (!draft) return null;

  const timeAgo = getTimeAgo(draft.lastSaved);
  const isRecent = Date.now() - draft.lastSaved < 5 * 60 * 1000; // 5 minutes

  const content = (
    <div className="flex items-center gap-1">
      <FileText className="h-3 w-3" />
      <span className="text-xs">Draft</span>
      {showTime && <span className="text-xs opacity-75">{timeAgo}</span>}
    </div>
  );

  const tooltipText = `Email Draft Available - Last saved ${new Date(draft.lastSaved).toLocaleString()}${draft.subject ? ` - Subject: ${draft.subject.substring(0, 30)}...` : ""} - Click to continue editing`;

  if (variant === "icon") {
    return (
      <TooltipWithContent content={tooltipText}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClick}
          className={cn("h-6 w-6 p-0", className)}
        >
          <FileText
            className={cn("h-4 w-4", isRecent ? "text-blue-600" : "text-muted-foreground")}
          />
        </Button>
      </TooltipWithContent>
    );
  }

  if (variant === "button") {
    return (
      <TooltipWithContent content={tooltipText}>
        <Button variant="outline" size="sm" onClick={onClick} className={cn("gap-2", className)}>
          {content}
        </Button>
      </TooltipWithContent>
    );
  }

  // Default badge variant
  return (
    <TooltipWithContent content={tooltipText}>
      <Badge
        variant={isRecent ? "default" : "secondary"}
        className={cn("cursor-pointer hover:opacity-80 transition-opacity gap-1", className)}
        onClick={onClick}
      >
        {content}
      </Badge>
    </TooltipWithContent>
  );
}

// Compact version for table rows and small spaces
export function CompactDraftIndicator({
  type,
  identifier,
  className,
  onClick,
}: Pick<DraftIndicatorProps, "type" | "identifier" | "className" | "onClick">) {
  return (
    <DraftIndicator
      type={type}
      identifier={identifier}
      variant="icon"
      showTime={false}
      className={className}
      onClick={onClick}
    />
  );
}

// Multi-draft indicator for showing multiple drafts
export function MultiDraftIndicator({
  drafts,
  className,
}: {
  drafts: EmailDraft[];
  className?: string;
}) {
  if (drafts.length === 0) return null;

  const recentDrafts = drafts.filter(
    (draft) => Date.now() - draft.lastSaved < 24 * 60 * 60 * 1000 // 24 hours
  );

  const multiDraftTooltip = `Recent Email Drafts: ${recentDrafts
    .slice(0, 3)
    .map(
      (draft) =>
        `${draft.type === "compose" ? `To: ${draft.to}` : "Reply draft"} - ${draft.subject || "No subject"}`
    )
    .join(", ")}${recentDrafts.length > 3 ? ` +${recentDrafts.length - 3} more` : ""}`;

  return (
    <TooltipWithContent content={multiDraftTooltip}>
      <Badge variant="secondary" className={cn("cursor-pointer gap-1", className)}>
        <Mail className="h-3 w-3" />
        <span className="text-xs">
          {recentDrafts.length} draft{recentDrafts.length !== 1 ? "s" : ""}
        </span>
      </Badge>
    </TooltipWithContent>
  );
}

// Utility function to format time ago
function getTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString();
}
